import {FC, useState} from 'react'
import {useHistory, useParams} from 'react-router-dom'
import classNames from 'classnames'
import {yup} from 'libs/yup'
import {yupResolver} from '@hookform/resolvers/yup'
import {useDispatch} from 'react-redux'
import {FormProvider, useForm, SubmitHandler} from 'react-hook-form'
import {Upload} from 'jbc-front/components/icons'
import Button from 'jbc-front/components/Button'
import Modal from 'jbc-front/components/CommonModal'
import {FileField} from 'components/reactHookForm/FileField'
import {useAsyncTask} from 'hooks/useAsyncTask'
import {useUploadR510Csv} from 'hooks/api/procedureStatuses/useUploadR510Csv'
import {asyncError} from 'store/actions/asyncError'
import styles from 'employees/FileImport.scss'
import Loading from 'components/Loading'

// @ts-ignore
import {Section} from 'jbc-front/components/Form'

const FileImportDescription = () => (
  <div className={styles.tableWrap}>
    <div className="l-overflow-scroll">
      <table className="m-table-description">
        <thead>
          <tr>
            <th className="m-table-description-item-name">データ項目名</th>
            <th>説明文</th>
            <th className="m-table-description-example">例</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <th>行番号</th>
            <td>
              入力情報を反映する賃金支払状況等の行番号を離職月を1行目とした通し番号で入力してください。17行目以降の入力は帳票では2枚目に記載され、ジョブカンでは31行まで登録することができます。
            </td>
            <td>17</td>
          </tr>
          <tr>
            <th>賃金支払基礎日数</th>
            <td>0~31の半角数字で入力</td>
            <td>31</td>
          </tr>
          <tr>
            <th>賃金支払対象期間開始日</th>
            <td>半角数字で入力 yyyy/mm/dd</td>
            <td>2021/01/01</td>
          </tr>
          <tr>
            <th>基礎日数</th>
            <td>0~31の半角数字で入力</td>
            <td>21</td>
          </tr>
          <tr>
            <th>賃金額（円）A欄</th>
            <td>
              月給の賃金を入力
              <br />
              ※通勤手当を3ヶ月または6ヶ月で支給している場合は当該月数で除した金額を各月に算入（端数は支給期間の最終月に計上）最後の月の給与を未計算で届出する場合は「未計算」と入力
            </td>
            <td>100000</td>
          </tr>
          <tr>
            <th>賃金額（円）B欄</th>
            <td>
              時間給、日給、出来高制の賃金を入力
              <br />
              最後の給与を未計算で届出する場合は「未計算」と入力
            </td>
            <td>50000</td>
          </tr>
          <tr>
            <th>備考欄</th>
            <td>16文字以内</td>
            <td>なし</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
)

const schema = yup
  .object({
    file: yup.mixed<File>().file().fileSize().required('ファイルを選択してください').label('ファイル'),
  })
  .required()

type Schema = yup.InferType<typeof schema>

interface ServerErrors {
  rows?: {index: number; name: string; message: string}[]
}

export const R510ImportPayments: FC = () => {
  const {id: procedureStatusId} = useParams<{id: string}>()
  const history = useHistory()
  const dispatch = useDispatch()
  const {taskRunningProps} = useAsyncTask()
  const [serverErrors, setServerErrors] = useState<ServerErrors | null>(null)

  const methods = useForm<Schema>({
    resolver: yupResolver(schema),
  })
  const {
    handleSubmit,
    watch,
    setValue,
    formState: {isSubmitting, errors},
  } = methods
  const file = watch('file')

  const uploadR510Csv = useUploadR510Csv()
  const onSubmit: SubmitHandler<Schema> = async (values) => {
    setServerErrors(null)
    try {
      await uploadR510Csv(procedureStatusId, values)
      history.push(`/procedure_statuses/${procedureStatusId}/report_edit#R510`)
    } catch (err) {
      if (err.response && err.response.status === 422 && err.response.data?._errors) {
        setServerErrors(err.response.data?._errors)
        return
      }
      dispatch(asyncError(err))
    }
  }

  return (
    <div>
      <div className="l-title-wrap u-pb30">
        <h1 className="m-title-main">
          雇用保険被保険者休業開始時賃金証明書
          <br />
          日数・賃金額の一括登録
        </h1>
      </div>
      {serverErrors?.rows && (
        <Section title="ファイル内容に誤りがあります" error>
          <ul>
            {serverErrors.rows.map((err, idx) => (
              <li key={idx}>
                第{err.index + 1}行：{err.name}
                {err.message}
              </li>
            ))}
          </ul>
        </Section>
      )}
      <div className={classNames(`u-mt30 u-ta-c`, styles.mainNoteWrap)}>
        <div className={styles.mainNoteTitle}>新規に登録したい賃金支払情報を記載したファイルを選択してください</div>
        <div className={styles.mainNoteBody}>
          <div className={styles.mainNote}>対応しているファイル形式はCSVとなります</div>
          <div className={styles.mainNote}>
            <a
              className="u-txt-link"
              href={`/api/procedure_statuses/${procedureStatusId}/report_param_sets/r510_csv`}
              rel="noopener"
              download
            >
              サンプルをダウンロード
            </a>
          </div>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className={classNames('u-mt20', styles.form)}>
              <div className="u-mb40 u-ta-l">
                <FileField name="file" isError={!!errors.file} error={errors.file?.message}>
                  {file && (
                    <FileField.FileContainer>
                      <FileField.File
                        file={file}
                        onRemove={() => {
                          // @ts-ignore
                          setValue('file', null)
                        }}
                      />
                    </FileField.FileContainer>
                  )}
                </FileField>
              </div>
              <Button
                as="button"
                primary
                type="submit"
                disabled={isSubmitting}
                icon={<Upload />}
                widthWide
                {...taskRunningProps}
              >
                登録
              </Button>
            </form>
            <Modal
              isOpen={isSubmitting}
              bodyOpenClassName={styles.loadingModalBody}
              className={styles.loadingModal}
              overlayClassName={styles.loadingModalOverlay}
            >
              <Modal.Body>
                <Loading />
              </Modal.Body>
            </Modal>
          </FormProvider>
        </div>
      </div>
      <div className={classNames(`u-mt30`, styles.warningWrap)}>
        <dt className={styles.warningTitle}>【注意】</dt>
        <dd>
          セルを空白の状態でアップロードすると、
          <span className={styles.warning}>該当箇所の登録データが削除されますのでご注意ください</span>
        </dd>
      </div>
      <div className="u-mt50">
        <FileImportDescription />
      </div>
    </div>
  )
}
